import { useState, useEffect, useRef } from 'react'
import type { Editor } from '@tiptap/react'
import {
  Modal,
  ModalFooter,
  Button,
  Input,
  Select,
  Checkbox,
  HtmlEditor,
  useToast,
} from '@orientamos/ui'
import {
  createClause,
  updateClause,
  getClause,
  type ClauseListItem,
  type ClauseCategory,
} from '@lib/api'

const PLACEHOLDER_GROUPS: { label: string; items: { key: string; label: string }[] }[] = [
  {
    label: 'Inmueble',
    items: [
      { key: 'property.address', label: 'Direccion' },
      { key: 'property.city', label: 'Ciudad' },
      { key: 'property.buildingName', label: 'Edificio/Conjunto' },
      { key: 'property.area', label: 'Area (m²)' },
      { key: 'property.stratum', label: 'Estrato' },
      { key: 'property.parking', label: 'Parqueaderos' },
      { key: 'property.storage', label: 'Depositos' },
    ],
  },
  {
    label: 'Terminos',
    items: [
      { key: 'terms.monthlyRent', label: 'Canon mensual' },
      { key: 'terms.administrationFee', label: 'Administracion' },
      { key: 'terms.durationMonths', label: 'Duracion (meses)' },
      { key: 'terms.startDate', label: 'Fecha inicio' },
      { key: 'terms.endDate', label: 'Fecha fin' },
      { key: 'terms.gracePeriodDays', label: 'Dias de gracia' },
    ],
  },
  {
    label: 'Arrendatario',
    items: [
      { key: 'tenant.fullName', label: 'Nombre completo' },
      { key: 'tenant.firstName', label: 'Nombre' },
      { key: 'tenant.lastName', label: 'Apellido' },
      { key: 'tenant.idNumber', label: 'Cedula' },
      { key: 'tenant.phone', label: 'Telefono' },
      { key: 'tenant.email', label: 'Email' },
      { key: 'tenant.address', label: 'Direccion' },
      { key: 'tenant.city', label: 'Ciudad' },
    ],
  },
  {
    label: 'Propietario',
    items: [
      { key: 'owner.fullName', label: 'Nombre completo' },
      { key: 'owner.firstName', label: 'Nombre' },
      { key: 'owner.lastName', label: 'Apellido' },
      { key: 'owner.idNumber', label: 'Cedula' },
      { key: 'owner.phone', label: 'Telefono' },
      { key: 'owner.email', label: 'Email' },
      { key: 'owner.address', label: 'Direccion' },
      { key: 'owner.city', label: 'Ciudad' },
    ],
  },
  {
    label: 'Deudor Solidario',
    items: [
      { key: 'guarantor.fullName', label: 'Nombre completo' },
      { key: 'guarantor.firstName', label: 'Nombre' },
      { key: 'guarantor.lastName', label: 'Apellido' },
      { key: 'guarantor.idNumber', label: 'Cedula' },
      { key: 'guarantor.phone', label: 'Telefono' },
      { key: 'guarantor.email', label: 'Email' },
    ],
  },
  {
    label: 'Contrato',
    items: [
      { key: 'orderNumber', label: 'Numero de orden' },
      { key: 'contractType', label: 'Tipo de contrato' },
      { key: 'guaranteedRent', label: 'Canon garantizado' },
    ],
  },
]

// Category labels in Spanish
const CATEGORY_OPTIONS: { value: ClauseCategory; label: string }[] = [
  { value: 'services', label: 'Servicios' },
  { value: 'grace_period', label: 'Periodo de Gracia' },
  { value: 'rent_increase', label: 'Incrementos' },
  { value: 'renewal', label: 'Renovacion' },
  { value: 'pets', label: 'Mascotas' },
  { value: 'modifications', label: 'Modificaciones' },
  { value: 'insurance', label: 'Seguros' },
  { value: 'utilities', label: 'Servicios Publicos' },
  { value: 'other', label: 'Otros' },
]

interface ClauseModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  clause?: ClauseListItem | null
}

export function ClauseModal({ open, onClose, onSuccess, clause }: ClauseModalProps) {
  const { addToast } = useToast()
  const isEditing = Boolean(clause)
  const editorRef = useRef<Editor | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showPlaceholders, setShowPlaceholders] = useState(false)

  // Form state
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState<ClauseCategory>('other')
  const [description, setDescription] = useState('')
  const [htmlContent, setHtmlContent] = useState('')
  const [residentialChecked, setResidentialChecked] = useState(true)
  const [commercialChecked, setCommercialChecked] = useState(true)

  // Load clause data when editing
  useEffect(() => {
    if (open && clause) {
      loadClauseDetails()
    } else if (open && !clause) {
      // Reset form for new clause
      setTitle('')
      setCategory('other')
      setDescription('')
      setHtmlContent(`<div class="clause">
  <h3>CLAUSULA ADICIONAL: [TITULO]</h3>
  <p>Texto de la clausula aqui...</p>
</div>`)
      setResidentialChecked(true)
      setCommercialChecked(true)
    }
  }, [open, clause])

  const loadClauseDetails = async () => {
    if (!clause) return

    setIsLoading(true)
    try {
      const result = await getClause(clause.id)
      if (result.success && result.data) {
        setTitle(result.data.title)
        setCategory(result.data.category)
        setDescription(result.data.description)
        setHtmlContent(result.data.htmlContent)
        setResidentialChecked(result.data.contractTypes.includes('residential'))
        setCommercialChecked(result.data.contractTypes.includes('commercial'))
      }
    } catch (err) {
      console.error('Error loading clause:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo cargar la clausula.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!title.trim()) {
      addToast({
        variant: 'error',
        message: 'El titulo es requerido.',
      })
      return
    }

    if (!description.trim()) {
      addToast({
        variant: 'error',
        message: 'La descripcion es requerida.',
      })
      return
    }

    if (!htmlContent.trim()) {
      addToast({
        variant: 'error',
        message: 'El contenido HTML es requerido.',
      })
      return
    }

    if (!residentialChecked && !commercialChecked) {
      addToast({
        variant: 'error',
        message: 'Selecciona al menos un tipo de contrato.',
      })
      return
    }

    const contractTypes: ('residential' | 'commercial')[] = []
    if (residentialChecked) contractTypes.push('residential')
    if (commercialChecked) contractTypes.push('commercial')

    setIsSaving(true)
    try {
      if (isEditing && clause) {
        const result = await updateClause(clause.id, {
          title,
          category,
          description,
          htmlContent,
          contractTypes,
        })
        if (result.success) {
          addToast({
            variant: 'success',
            message: 'Clausula actualizada. Los cambios han sido guardados.',
          })
          onSuccess()
        } else {
          throw new Error(result.error)
        }
      } else {
        const result = await createClause({
          title,
          category,
          description,
          htmlContent,
          contractTypes,
        })
        if (result.success) {
          addToast({
            variant: 'success',
            message: 'Clausula creada exitosamente.',
          })
          onSuccess()
        } else {
          throw new Error(result.error)
        }
      }
    } catch (err) {
      console.error('Error saving clause:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo guardar la clausula.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Editar Clausula' : 'Nueva Clausula'}
      size="xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Title */}
            <Input
              label="Titulo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Periodo de Gracia Extendido"
              required
            />

            {/* Category */}
            <Select
              label="Categoria"
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={(e) => setCategory(e.target.value as ClauseCategory)}
            />

            {/* Description */}
            <Input
              label="Descripcion"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descripcion de la clausula"
              required
            />

            {/* Contract Types */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Tipos de Contrato
              </label>
              <div className="flex gap-6">
                <Checkbox
                  label="Residencial"
                  checked={residentialChecked}
                  onChange={(e) => setResidentialChecked(e.target.checked)}
                />
                <Checkbox
                  label="Comercial"
                  checked={commercialChecked}
                  onChange={(e) => setCommercialChecked(e.target.checked)}
                />
              </div>
            </div>

            {/* HTML Content */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Contenido HTML
              </label>
              <HtmlEditor value={htmlContent} onChange={setHtmlContent} editorRef={editorRef} />
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowPlaceholders(!showPlaceholders)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  {showPlaceholders ? 'Ocultar campos dinamicos' : 'Insertar campo dinamico'}{' '}
                  <span className="text-[10px]">{showPlaceholders ? '▲' : '▼'}</span>
                </button>
                {showPlaceholders && (
                  <div className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3 max-h-56 overflow-y-auto">
                    <p className="text-xs text-neutral-500 mb-2">
                      Haz clic en un campo para insertarlo en el editor.
                    </p>
                    <div className="space-y-3">
                      {PLACEHOLDER_GROUPS.map((group) => (
                        <div key={group.label}>
                          <p className="text-xs font-semibold text-neutral-600 mb-1">
                            {group.label}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {group.items.map((item) => (
                              <button
                                key={item.key}
                                type="button"
                                onClick={() => {
                                  const editor = editorRef.current
                                  if (editor) {
                                    editor.chain().focus().insertContent(`{{${item.key}}}`).run()
                                  }
                                }}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-white border border-neutral-300 text-neutral-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-colors"
                                title={`{{${item.key}}}`}
                              >
                                {item.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <ModalFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={isSaving}>
              {isEditing ? 'Guardar Cambios' : 'Crear Clausula'}
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  )
}
